import styles from './MetricCell.module.css';

/**
 * One tone per metric, so a column keeps the same colour down its whole length
 * and the eye can track it. Mirrors the D360 cell convention (gold revenue,
 * cyan acquisition, purple retention) using our own tokens.
 */
export type MetricTone = 'revenue' | 'share' | 'acquired' | 'retained' | 'neutral' | 'decline';

export interface MetricCellProps {
  /** Preformatted display value, e.g. "$75.6M". */
  value: string;
  /** 0–1 magnitude relative to the column's max. Omit to hide the bar. */
  fraction?: number;
  tone?: MetricTone;
  /** Secondary line under the bar — a tier word, a delta, a unit. */
  sub?: string;
  /** Colour the sub line by sentiment rather than by column tone. */
  subTone?: 'good' | 'bad' | 'muted';
  align?: 'left' | 'right';
}

/**
 * The table's unit of meaning: a value, its magnitude, and an optional label —
 * always in the same place, at a fixed height. Columns built from these can be
 * compared by scanning rather than by reading every number.
 */
export function MetricCell({
  value,
  fraction,
  tone = 'neutral',
  sub,
  subTone = 'muted',
  align = 'left',
}: MetricCellProps) {
  return (
    <div className={`${styles.cell} ${styles[align]}`}>
      <span className={styles.value}>{value}</span>

      {fraction != null && (
        <span className={styles.track}>
          <span
            className={`${styles.fill} ${styles[tone]}`}
            style={{ width: `${Math.max(0, Math.min(1, fraction)) * 100}%` }}
          />
        </span>
      )}

      {sub && <span className={`${styles.sub} ${styles[subTone]}`}>{sub}</span>}
    </div>
  );
}
